import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import styled from "styled-components";

import {
  faBus,
  faCheck,
  faPencil,
  faPlus,
  faTrash,
  faUndo,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Button, PopoverBody } from "react-bootstrap";

import olFeature, { FeatureLike as olFeatureLike } from "ol/Feature";
import { Extent as olExtent } from "ol/extent";
import GeoJSON from "ol/format/GeoJSON.js";
import olPoint from "ol/geom/Point";
import olPolygon from "ol/geom/Polygon";
import olClusterSource from "ol/source/Cluster";
import olOSMSource from "ol/source/OSM";
import olVectorSource from "ol/source/Vector";

import { OLParser } from "../../components/reactOL/JSTS/OL";
import {
  Controls,
  FullScreen,
  ScaleBar,
  ToolBar,
  ZoomButtons,
  ZoomSlide,
} from "../../components/reactOL/controls";
import {
  DrawPolygon,
  EditPolygon,
  Interactions,
  MapClick,
  ZoomToCluster,
} from "../../components/reactOL/interactions";
import { Layers, Mask, Tile, Vector } from "../../components/reactOL/layers";
import { Map } from "../../components/reactOL/map";
import { Overlays, PopupOverlay } from "../../components/reactOL/overlays";
import { Epsg27700 } from "../../components/reactOL/projections";
import ConstrainedView from "../../components/reactOL/views/ConstrainedView";
import {
  BoundaryStyle,
  ClusterStyle,
  MaskFill,
  StationStyle,
} from "../../theme/mapStyles";

import IconButton from "../../@ui/IconButton";
import ConfirmDeleteForm from "../../components/Forms/ConfirmDeleteForm";
import FareZoneForm from "../../components/Forms/FareZoneForm";
import { useAlertContext } from "../../components/alert/AlertContext";
import { useAuthContext } from "../../components/auth/AuthContext";
import CancelableSelect from "../../components/cancelableSelect/CancelableSelect";
import SubHeader from "../../components/subheader/SubHeader";
import { AlertType } from "../../types/AlertMessage";

import FareZoneService from "../../services/FareZoneService";
import { DoesUserHaveRole } from "../../services/LoggedInUserService";

import { Roles } from "../../constant/Roles";
import { ToolsType } from "../../constant/Tools";
import StopService from "../../services/StopService";
import { FareOperation } from "../../types/AddFare";
import { AddFareZone } from "../../types/AddFareZone";
import { EditFareZone } from "../../types/EditFareZone";
import { FareZone } from "../../types/FareZone";

const Filters = styled.div`
  display: flex;
  flex-direction: row;
`;
const Container = styled.div`
  height: 100vh;
  display: flex;
  flex-direction: column;
  min-height: 0;
`;

const MapContainer = styled.div`
  position: relative;
  flex: 1;
`;

const DrawControls = styled.div`
  position: absolute;
  bottom: 0;
  left: 50%;
  transform: translateX(-50%);
`;

function Admin() {
  const navigate = useNavigate();
  const authContext = useAuthContext();
  const alertContext = useAlertContext();

  const { fareZoneId } = useParams();

  const fareZoneService = new FareZoneService(alertContext.showAlert);
  const stopService = new StopService(alertContext.showAlert);
  const formatter = new GeoJSON();

  const [selectedInteraction, setSelectedInteraction] = useState<ToolsType>(
    ToolsType.NONE
  );
  const [editFareZoneModel, setEditFareZoneModel] = useState<
    AddFareZone | EditFareZone
  >();
  const [mode, setMode] = useState<"Add" | "Edit" | "View">("View");
  const [lastEditBoundary, setLastEditBoundary] = useState<
    olPolygon | undefined
  >();
  const [showFareZoneForm, setShowFareZoneForm] = useState<boolean>(false);
  const [showConfirmDeleteForm, setShowConfirmDeleteForm] =
    useState<boolean>(false);
  const [userIsAdmin, setUserIsAdmin] = useState<boolean>(false);
  const [fareZoneList, setFareZoneList] = useState<
    { value: string; label: string }[]
  >([]);
  const [selectedFareZone, setSelectedFareZone] = useState<FareZone>();
  const [selectedFareZoneFeature, setSelectedFareZoneFeature] =
    useState<olFeature>();
  const [selectedStop, setSelectedStop] = useState<olFeature | undefined>();

  const view = useMemo(
    () =>
      new ConstrainedView({
        center: [451073, 324546],
        zoom: 5,
        projection: Epsg27700,
        minZoom: 5,
        maxZoom: 15,
      }),
    []
  );
  const toolsSource = useMemo(() => new olVectorSource(), []);
  const boundaryVectorSource = useMemo(() => new olVectorSource(), []);
  const stationsVectorSource = useMemo(() => new olVectorSource(), []);
  const stopVectorSource = useMemo(() => new olVectorSource(), []);
  const stopClusterSource = useMemo(
    () =>
      new olClusterSource({
        distance: 15,
        minDistance: 15,
        source: stopVectorSource,
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );
  const osm = useMemo(() => new olOSMSource(), []);

  useEffect(() => {
    if (DoesUserHaveRole(authContext.user, Roles.Admin)) {
      setUserIsAdmin(true);
    } else {
      setUserIsAdmin(false);
    }
    UpdateFareZoneList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setMode("View");
    if (fareZoneId === undefined) return;
    getFareZone(fareZoneId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fareZoneId]);

  useEffect(() => {
    if (DoesUserHaveRole(authContext.user, Roles.Admin)) {
      setUserIsAdmin(true);
    } else {
      setUserIsAdmin(false);
    }
  }, [authContext.user]);

  const UpdateFareZoneList = () => {
    fareZoneService.getAll(true).then((r) => {
      const options = r.map((fareZone) => ({
        value: fareZone.id,
        label: fareZone.name,
      }));
      setFareZoneList(options);
    });
  };

  const getFareZone = (fareZoneId: string) => {
    fareZoneService.getById(fareZoneId, true).then((fareZone) => {
      if (!fareZone) return;
      const fareZoneFeature = new olFeature({
        ...fareZone,
        boundary: null,
        geometry: new olPolygon(fareZone.boundary.coordinates),
      });

      // If there is an edit boundary feature already remove it
      if (boundaryVectorSource.getFeatures().length > 0) {
        boundaryVectorSource.clear();
      }

      boundaryVectorSource.addFeature(fareZoneFeature);
      setSelectedFareZone(fareZone);
      setSelectedFareZoneFeature(fareZoneFeature);

      const fareZoneExtent = fareZoneFeature
        .getGeometry()
        ?.getExtent() as olExtent;
      view.fit(fareZoneExtent);
      view.ConstrainToExtent = fareZoneExtent;

      getStations(fareZone);

      getStopsInFareZone(fareZoneId);
    });
  };

  const getStations = (fareZone: FareZone) => {
    const features = fareZone.schemes
      .map((x) => x.stations)
      .reduce(
        (accumulator, currentArray) => [...accumulator, ...currentArray],
        []
      )
      .map(
        (station) =>
          new olFeature({
            ...station,
            location: null,
            geometry: new olPoint(station.location.coordinates),
          })
      );

    stationsVectorSource.clear();
    stationsVectorSource.addFeatures(features);
  };

  const getStopsInFareZone = (fareZoneId: string) => {
    fareZoneService.getStopsInFareZone(fareZoneId, true).then((response) => {
      if (!response) return;
      const features = response.features.map((feature: any) => {
        const f = new olFeature({
          geometry: new olPoint(feature.geometry.coordinates),
          id: feature.properties.Id,
          name: feature.properties.Name,
          naptanCode: feature.properties.NaptanCode,
        });
        return f;
      });
      stopVectorSource.addFeatures(features);
    });
  };

  const getStopsInBoundary = (polygon: olPolygon) => {
    const boundary = formatter.writeGeometryObject(polygon, {
      dataProjection: Epsg27700,
      featureProjection: Epsg27700,
    });

    stopService.withinGeometry(boundary).then((response) => {
      if (!response) return;

      stopVectorSource.clear();
      const features = response.features.map((feature: any) => {
        const f = new olFeature({
          geometry: new olPoint(feature.geometry.coordinates),
          id: feature.properties.Id,
          name: feature.properties.Name,
          naptanCode: feature.properties.NaptanCode,
        });
        return f;
      });
      stopVectorSource.addFeatures(features);
    });
  };

  const adminAreaSelection = (id: string | undefined) => {
    view.ConstrainToExtent = undefined;
    stopVectorSource.clear();
    boundaryVectorSource.clear();
    setSelectedFareZoneFeature(undefined);
    setSelectedFareZone(undefined);
    setEditFareZoneModel(undefined);
    setLastEditBoundary(undefined);
    setSelectedInteraction(ToolsType.NONE);
    setMode("View");

    if (id) {
      navigate(`/Admin/${id}`);
    } else {
      navigate(`/Admin`);
    }
  };

  const handleCommandAdd = () => {
    if (mode === "Add") {
      handleDrawRevert();
    } else {
      setSelectedInteraction(ToolsType.DRAW_POLYGON);
      setMode("Add");
    }
  };

  const handleCommandEdit = () => {
    if (!selectedFareZone) return;
    setMode("Edit");
    const model: EditFareZone = {
      name: selectedFareZone.name,
      schemeCoordinatorId: selectedFareZone.schemeCoordinator.id,
      boundary: selectedFareZone.boundary,
      schemes: selectedFareZone.schemes,
      operatorNOCs: selectedFareZone.operators.map((operator) => operator.noc),
      lastModifiedDate: selectedFareZone.lastModifiedDate,
    };
    setEditFareZoneModel(model);
    setShowFareZoneForm(true);
  };

  const handleEditBoundary = (updatedModel: EditFareZone) => {
    if (!selectedFareZoneFeature) return;
    setShowFareZoneForm(false);
    const currentBoundary = selectedFareZoneFeature.getGeometry() as olPolygon;
    setEditFareZoneModel(updatedModel);
    setLastEditBoundary(currentBoundary.clone());
    setSelectedInteraction(ToolsType.EDIT_POLYGON);
  };

  const polygonDrawnHandler = (polygon: jsts.geom.Polygon) => {
    getStopsInBoundary(OLParser.write(polygon));
    const feature = new olFeature({
      name: "",
      schemeCoordinatorId: "",
      exclusions: "",
      stationNLCs: [],
      operatorNOCs: [],
      geometry: OLParser.write(polygon),
    });
    boundaryVectorSource.addFeature(feature);
    setSelectedFareZoneFeature(feature);
    setSelectedInteraction(ToolsType.EDIT_POLYGON);
  };

  const polygonEditHandler = (polygon: jsts.geom.Polygon) => {
    getStopsInBoundary(OLParser.write(polygon));
  };

  const handeDrawAccept = () => {
    // This is true of add or edit
    var drawnFeature = boundaryVectorSource.getFeatures()[0];
    if (!drawnFeature) {
      alertInvalidBoundary();
      return;
    }
    var drawnGeometry = drawnFeature?.getGeometry() as olPolygon;
    if (!drawnGeometry) return;

    const jstsGeom = OLParser.read(
      drawnFeature.getGeometry()
    ) as jsts.geom.Polygon;

    // check valid geometry
    if (!jstsGeom.isValid()) {
      alertInvalidBoundary();
      return;
    }
    if (mode === "Add") {
      const model = {
        ...drawnFeature.getProperties(),
        fare: {
          effectiveFrom: defaultEffectiveFrom.toJSON(),
          operation: FareOperation.Add,
        },
        boundary: formatter.writeGeometryObject(drawnGeometry, {
          dataProjection: Epsg27700,
          featureProjection: Epsg27700,
        }),
      } as any;
      delete model.geometry;
      setEditFareZoneModel(model);
    } else if (mode === "Edit" && editFareZoneModel) {
      setLastEditBoundary(drawnGeometry.clone());
      editFareZoneModel.boundary = formatter.writeGeometryObject(
        drawnGeometry,
        {
          dataProjection: Epsg27700,
          featureProjection: Epsg27700,
        }
      );
      setEditFareZoneModel(editFareZoneModel);
    }

    setSelectedInteraction(ToolsType.NONE);
    setShowFareZoneForm(true);

    setLastEditBoundary(undefined);
  };

  const handleDrawRevert = () => {
    setSelectedInteraction(ToolsType.NONE);
    if (mode === "Add") {
      stopVectorSource.clear();
      boundaryVectorSource.clear();
      setSelectedFareZoneFeature(undefined);
      setMode("View");
    } else if (mode === "Edit") {
      // revert the feature's boundary to the last value accepted
      if (lastEditBoundary) {
        var editFeature = boundaryVectorSource.getFeatures()[0];
        editFeature.setGeometry(lastEditBoundary);
        getStopsInBoundary(lastEditBoundary);
      }
      setShowFareZoneForm(true);
    }
  };

  const alertInvalidBoundary = () => {
    alertContext.showAlert({
      message: "Invalid boundary drawn",
      type: AlertType.Error,
    });
  };

  const handleFareZoneFormClose = () => {
    boundaryVectorSource.clear();
    if (mode === "Add") {
      stopVectorSource.clear();
      boundaryVectorSource.clear();
      setSelectedFareZoneFeature(undefined);
    } else if (mode === "Edit" && selectedFareZone) {
      // revert the feature's boundary back to the original model
      const revertedPolygon = new olPolygon(
        selectedFareZone.boundary.coordinates
      );
      const revertedFeature = new olFeature({
        ...selectedFareZone,
        boundary: null,
        geometry: revertedPolygon,
      });
      getStopsInBoundary(revertedPolygon);
      setSelectedFareZoneFeature(revertedFeature);
      boundaryVectorSource.addFeature(revertedFeature);
    }
    setSelectedInteraction(ToolsType.NONE);
    setMode("View");
    setShowFareZoneForm(false);
  };

  const handlePostCommit = (id?: string) => {
    setSelectedInteraction(ToolsType.NONE);
    setShowFareZoneForm(false);
    setShowConfirmDeleteForm(false);
    if (mode === "Edit" && id) {
      getFareZone(id);
      UpdateFareZoneList();
    } else {
      UpdateFareZoneList();
      adminAreaSelection(id);
    }

    setMode("View");
  };

  const stopClickHandler = (features: olFeatureLike[]) => {
    let selectedStop;
    if (features.length) {
      var clusterFeatures = features[0].getProperties().features;
      if (clusterFeatures.length === 1) {
        selectedStop = clusterFeatures[0];
      }
    }
    setSelectedStop(selectedStop);
  };

  const today = new Date();
  const minEffectiveFrom = new Date(
    Date.UTC(today.getFullYear(), today.getMonth(), today.getDate())
  );

  minEffectiveFrom.setHours(0);

  if (!userIsAdmin) {
    minEffectiveFrom.setDate(minEffectiveFrom.getDate() + 7);
  }

  const defaultEffectiveFrom = new Date(
    Date.UTC(today.getFullYear(), today.getMonth(), today.getDate())
  );
  defaultEffectiveFrom.setMonth(defaultEffectiveFrom.getMonth() + 2);

  return (
    <Container>
      <SubHeader className="bg-tertiary">
        <Filters>
          <CancelableSelect
            options={fareZoneList}
            placeHolder={"Select a zone"}
            onSelect={adminAreaSelection}
            value={fareZoneId}
            disabled={fareZoneList.length === 0}
          />
        </Filters>
      </SubHeader>

      <MapContainer>
        <Map view={view}>
          <Layers>
            <Tile source={osm} />
            <Vector
              name="boundary"
              source={boundaryVectorSource}
              style={BoundaryStyle}
            />
            <Mask feature={selectedFareZoneFeature} fill={MaskFill} />
            <Vector
              name="stops"
              source={stopClusterSource}
              style={ClusterStyle}
            />
            <Vector
              name="stations"
              source={stationsVectorSource}
              style={StationStyle}
            />
          </Layers>
          <Controls>
            <ScaleBar />
            <FullScreen />
            <ZoomButtons />
            <ZoomSlide />
            <ToolBar className={"toolbar card"}>
              {userIsAdmin && (
                <IconButton
                  onClick={handleCommandAdd}
                  title="Add Fare Zone"
                  icon={faPlus}
                  disabled={selectedFareZone !== undefined}
                  active={mode === "Add"}
                />
              )}
              <IconButton
                onClick={handleCommandEdit}
                title="Edit Fare Zone"
                icon={faPencil}
                disabled={selectedFareZone === undefined}
                active={mode === "Edit"}
              />
              {userIsAdmin && (
                <IconButton
                  onClick={() => setShowConfirmDeleteForm(true)}
                  title="Delete Fare Zone"
                  icon={faTrash}
                  disabled={selectedFareZoneFeature === undefined}
                />
              )}
            </ToolBar>
          </Controls>
          <Interactions>
            <DrawPolygon
              source={toolsSource}
              active={selectedInteraction === ToolsType.DRAW_POLYGON}
              onDrawEnd={polygonDrawnHandler}
            />
            <EditPolygon
              source={boundaryVectorSource}
              active={selectedInteraction === ToolsType.EDIT_POLYGON}
              onDrawEnd={polygonEditHandler}
            />
            <ZoomToCluster />
            <MapClick
              active={fareZoneId !== undefined}
              onClick={stopClickHandler}
              layerNames={["stops"]}
            />
          </Interactions>
          <Overlays>
            <PopupOverlay
              coordinate={(
                selectedStop?.getGeometry() as olPoint | undefined
              )?.getCoordinates()}
              placement="top"
            >
              <PopoverBody className="p-0" style={{ borderRadius: 7 }}>
                <div
                  className="bg-info text-white py-0 px-2 text-nowrap"
                  style={{ borderTopLeftRadius: 7, borderTopRightRadius: 7 }}
                >
                  <FontAwesomeIcon icon={faBus} />{" "}
                  {selectedStop?.getProperties().name}
                </div>
                <div className="text-info py-0 px-2 text-nowrap">
                  Stop Code:{" "}
                  {selectedStop?.getProperties().naptanCode ?? "unknown"}
                </div>
              </PopoverBody>
            </PopupOverlay>
          </Overlays>
        </Map>

        {selectedInteraction !== ToolsType.NONE && (
          <DrawControls>
            <Button onClick={handeDrawAccept} className="me-1 mb-1">
              <FontAwesomeIcon icon={faCheck} /> Accept
            </Button>
            <Button
              onClick={handleDrawRevert}
              variant="danger"
              className="me-1 mb-1"
            >
              <FontAwesomeIcon icon={faUndo} /> Revert
            </Button>
          </DrawControls>
        )}

        {showFareZoneForm && editFareZoneModel && (
          <FareZoneForm
            model={editFareZoneModel}
            fareZone={selectedFareZone}
            onClose={handleFareZoneFormClose}
            onEditBoundary={handleEditBoundary}
            onSubmit={handlePostCommit}
            minEffectiveFrom={minEffectiveFrom}
            suggestedEffectiveFrom={defaultEffectiveFrom}
          />
        )}

        {showConfirmDeleteForm && selectedFareZone && (
          <ConfirmDeleteForm
            fareZone={selectedFareZone}
            onClose={() => setShowConfirmDeleteForm(false)}
            onDeleteFareZone={handlePostCommit}
          />
        )}
      </MapContainer>
    </Container>
  );
}

export default Admin;
